import { Checkbox, Hint, InputLabel } from '@expane/ui'
import { FC } from 'react'
import { Control, Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useBusinessModulesSettings } from '@expane/logic/modules'
import {
  PaymentProviderFormValues,
  useIsPaymentProviderConfigInUse,
} from '@expane/logic/payment/providers'
import { store } from 'store'

interface PaymentProviderItemsConfigProps {
  control: Control<PaymentProviderFormValues>
  isEditingAllowed: boolean
  paymentProviderId: string | undefined
}

export const PaymentProviderItemsConfig: FC<PaymentProviderItemsConfigProps> = ({
  control,
  isEditingAllowed,
  paymentProviderId,
}) => {
  const branchId = store.branch.branchId

  const { t } = useTranslation()

  const { getModuleSetting } = useBusinessModulesSettings()

  const areGiftCardsEnabled = getModuleSetting('giftCards')
  const areSubscriptionsEnabled = getModuleSetting('subscriptions')

  const watchedType = useWatch({ control, name: 'type' })

  const { isBookingConfigInUse, isSubscriptionConfigInUse, isGiftCardConfigInUse } =
    useIsPaymentProviderConfigInUse(paymentProviderId, watchedType, branchId)

  return (
    <div>
      <InputLabel
        className="mb-2"
        label={t('paymentProvider.paymentItemsConfig')}
        hint={t('paymentProvider.paymentItemsConfigHint')}
      />

      <div className="flex gap-2">
        {isBookingConfigInUse ? (
          <div className={'flex gap-2 items-center'}>
            <Hint type="warning">{t('paymentProvider.paymentItemsConfigWarningHint')}</Hint>
            <InputLabel label={t('booking.name')} />
          </div>
        ) : (
          <Controller
            control={control}
            name="paymentItemsConfig.booking"
            render={({ field: { value, onChange } }) => (
              <Checkbox
                label={t('booking.name')}
                checked={value}
                onChange={onChange}
                disabled={!isEditingAllowed}
              />
            )}
          />
        )}

        {areSubscriptionsEnabled ? (
          !isSubscriptionConfigInUse ? (
            <Controller
              control={control}
              name="paymentItemsConfig.subscription"
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  label={t('subscription.name')}
                  checked={value}
                  onChange={onChange}
                  disabled={!isEditingAllowed}
                />
              )}
            />
          ) : (
            <div className={'flex gap-2 items-center'}>
              <Hint type="warning">{t('paymentProvider.paymentItemsConfigWarningHint')}</Hint>
              <InputLabel label={t('subscription.name')} />
            </div>
          )
        ) : null}

        {areGiftCardsEnabled ? (
          !isGiftCardConfigInUse ? (
            <Controller
              control={control}
              name="paymentItemsConfig.giftCard"
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  label={t('giftCard.name')}
                  checked={value}
                  onChange={onChange}
                  disabled={!isEditingAllowed}
                />
              )}
            />
          ) : (
            <div className={'flex gap-2 items-center'}>
              <Hint type="warning">{t('paymentProvider.paymentItemsConfigWarningHint')}</Hint>
              <InputLabel label={t('giftCard.name')} />
            </div>
          )
        ) : null}
      </div>
    </div>
  )
}
